import React, { useEffect, useState } from "react";
import { useSiteContext } from "../SiteContext";
import { generateWCDPBatchExportForEmployees } from "../api/v2BatchExport";
import { EmployeeTimeSheet, Supervisor } from "../interfaces/V2Interface";
import V2BatchExportManager from "../managers/V2BatchExportManager";

interface PopupProps {
  isModalVisible: boolean;
  closeModal: () => void;
  popupTitle: string;
  popupMessage: string;
}

interface V2BatchExportModalProps {
  visible: boolean;
  onClose: () => void;
  supervisorName: string;
  supervisorId: string;
  supervisorAltId: string;
  startDate: string;
  endDate: string;
  createPopup: (title: string, message: string) => void;
}

// Loading component
const LoadingSpinner: React.FC<{ message: string }> = ({ message }) => (
  <div className="loading-container">
    <div className="loading-spinner"></div>
    <p className="loading-message">{message}</p>
  </div>
);

const V2BatchExportModal: React.FC<V2BatchExportModalProps> = ({
  visible,
  onClose,
  supervisorName,
  supervisorId,
  supervisorAltId,
  startDate,
  endDate,
  createPopup,
}) => {
  const { payPeriodID, payPeriods, translator } = useSiteContext();
  const [employees, setEmployees] = useState<EmployeeTimeSheet[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(true);
  const [batchData, setBatchData] = useState<{ batchID: string; batchID_DD: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchingEmployees, setFetchingEmployees] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [batchReports, setBatchReports] = useState<any>(null);

  // Find the current pay period to get the week number
  const currentPayPeriod = payPeriods.find(pp => pp.PayPeriodID === payPeriodID);
  const weekNumber = currentPayPeriod?.WeekNumber || 0;

  // Helper function to format employee name
  const formatName = (name: { first: string; middle: string; last: string } | string) => {
    if (typeof name === 'string') return name;
    return `${name.first} ${name.middle ? name.middle + ' ' : ''}${name.last}`;
  };

  // Reset modal state
  const resetModal = () => {
    setBatchData(null);
    setSelectedEmployees(new Set());
    setSelectAll(true);
    setError(null);
    setFetchError(null);
    setEmployees([]);
    setFetchingEmployees(true);
    setLoading(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedEmployees(new Set());
    } else {
      const allIDs = new Set(employees.map((emp) => emp.employeeID || emp.employeeID.toString()));
      setSelectedEmployees(allIDs);
    }
    setSelectAll(!selectAll);
  };

  const handleCheckboxChange = (employeeID: string) => {
    setSelectedEmployees((prev) => {
      const updated = new Set(prev);
      updated.has(employeeID) ? updated.delete(employeeID) : updated.add(employeeID);
      setSelectAll(updated.size === employees.length);
      return updated;
    });
  };

  const handleConfirmExport = async () => {
    if (selectedEmployees.size === 0) {
      setError(translator.translate("PleaseSelectEmployee"));
      createPopup(translator.translate("ExportError"), translator.translate("PleaseSelectEmployee"));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const altEmployeeIDs = employees
        .filter(emp => selectedEmployees.has(emp.employeeID))
        .map(emp => emp.altEmployeeID);

      const batchData = await generateWCDPBatchExportForEmployees(
        parseInt(supervisorId),
        payPeriodID,
        altEmployeeIDs
      );
      setBatchData(batchData);

      // Fetch batch reports after successful export
      const reports = await V2BatchExportManager.getBatchReports(batchData.batchID);
      setBatchReports(reports);
    } catch (error) {
      console.error("Error in batch export confirm:", error);
      const errorMessage = error instanceof Error ? error.message : translator.translate("FailedCompleteBatchExport");
      setError(errorMessage);
      createPopup(translator.translate("ExportError"), translator.translate("FailedCompleteBatchExport"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && supervisorId && payPeriodID) {
      const fetchEmployees = async () => {
        try {
          setFetchError(null);
          setFetchingEmployees(true);
          
          const employeesData = await V2BatchExportManager.getEmployees(
            supervisorId,
            payPeriodID
          );

          employeesData.sort((a: EmployeeTimeSheet, b: EmployeeTimeSheet) => {
            const altA = parseInt(a.altEmployeeID || '0', 10) || 0;
            const altB = parseInt(b.altEmployeeID || '0', 10) || 0;
            return altA - altB;
          })
          
          if (!employeesData || employeesData.length === 0) {
            throw new Error(translator.translate("NoEmployeesFoundSupervisor"));
          }
          
          setEmployees(employeesData);
          
          const allIDs = new Set(employeesData.map((emp) => emp.employeeID || emp.employeeID.toString()));
          setSelectedEmployees(allIDs);
          setSelectAll(true);
          
        } catch (err) {
          console.error("Failed to fetch data:", err);
          const errorMessage = err instanceof Error ? err.message : translator.translate("FailedFetchEmployeeData");
          setFetchError(errorMessage);
          createPopup(translator.translate("FetchError"), translator.translate("FailedFetchEmployeeData"));
          setEmployees([]);
          setSelectedEmployees(new Set());
        } finally {
          setFetchingEmployees(false);
        }
      };

      fetchEmployees();
    }
}, [visible, supervisorId, payPeriodID, startDate, endDate, translator]);

  if (!visible) return null;

  return (
    <div className="batch-export-modal-overlay">
      <div className="batch-export-modal" onClick={(e) => e.stopPropagation()}>
        {fetchingEmployees ? (
          <LoadingSpinner message={translator.translate("LoadingEmployees")} />
        ) : loading ? (
          <LoadingSpinner message={translator.translate("ProcessingExport")} />
        ) : (
          <>
            <h2 className="batch-export-title">
              {translator.translate("ExportBatchFor")} {supervisorName}
            </h2>

            <div className="batch-export-content">
              <p>{translator.translate("SupervisorID")} {supervisorAltId}</p>
              <p>{translator.translate("Week")} {weekNumber}</p>
              
              {/* Show fetch error if it exists */}
              {fetchError && (
                <div className="batch-export-error">
                  <p>{translator.translate("ErrorLoadingEmployees")} {fetchError}</p>
                </div>
              )}
              
              {/* Show export error if it exists */}
              {error && !fetchError && <p className="batch-export-error">{error}</p>}
            </div>

            {!batchData ? (
              <>
                {employees.length > 0 && (
                  <>
                    <div className="batch-export-select-all">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAllChange}
                        id="batch-export-select-all"
                        disabled={loading || employees.length === 0}
                      />
                      <label htmlFor="batch-export-select-all">
                        {translator.translate("SelectAll")} ({selectedEmployees.size} of {employees.length} {translator.translate("Selected")})
                      </label>
                    </div>

                    <div className="batch-export-employee-list-container">
                      <ul className="batch-export-employee-list">
                        {employees.map((emp) => (
                          <li key={emp.employeeID} className="batch-export-employee-item">
                            <label>
                              <input
                                type="checkbox"
                                checked={selectedEmployees.has(emp.employeeID)}
                                onChange={() => handleCheckboxChange(emp.employeeID)}
                                disabled={loading}
                              />
                              {formatName(emp.name)} ({translator.translate("EmployeeID")} {emp.altEmployeeID})
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}

                <div className="batch-export-modal-footer">
                  {employees.length > 0 && (
                    <button 
                      className="batch-export-button" 
                      onClick={handleConfirmExport}
                      disabled={loading || selectedEmployees.size === 0}
                    >
                      {translator.translate("ExportSelected")} ({selectedEmployees.size})
                    </button>
                  )}
                  <button 
                    className="batch-export-cancel" 
                    onClick={handleClose}
                    disabled={loading}
                  >
                    {employees.length === 0 ? translator.translate("Close") : translator.translate("Cancel")}
                  </button>
                </div>
              </>
            ) : (
              <>
                {batchReports && batchReports.regularReport && batchReports.ddReport && (
                  (() => {
                    const BASE_DOWNLOAD_URL = "https://admin.api.wcdp.com";
                    const regularPath = batchReports.regularReport.data.replace(/\\/g, '/');
                    const ddPath = batchReports.ddReport.data.replace(/\\/g, '/');
                    const regularLink = `${BASE_DOWNLOAD_URL}${regularPath}`;
                    const ddLink = `${BASE_DOWNLOAD_URL}${ddPath}`;
                    return (
                      <div className="batch-export-links" style={{ marginBottom: '18px', display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                        <a
                          href={regularLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#1976d2', fontWeight: 500, display: 'inline' }}
                        >
                          Regular Report
                        </a>
                        <a
                          href={ddLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#1976d2', fontWeight: 500, display: 'inline' }}
                        >
                          Direct Deposit Report
                        </a>
                      </div>
                    );
                  })()
                )}
                
                <div className="batch-export-success">
                  <p>{translator.translate("BatchExportSuccessful")}</p>
                  <div className="batch-export-batch-ids">
                    <div className="batch-export-field">
                      <label>{translator.translate("BatchID")}</label>
                      <input
                        type="text"
                        value={batchData.batchID}
                        readOnly
                        className="batch-export-textbox"
                      />
                    </div>
                    <div className="batch-export-field">
                      <label>{translator.translate("BatchIDDirectDeposit")}</label>
                      <input
                        type="text"
                        value={batchData.batchID_DD}
                        readOnly
                        className="batch-export-textbox"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="batch-export-modal-footer">
                  <button 
                    className="batch-export-button" 
                    onClick={handleClose}
                  >
                    {translator.translate("Close")}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles = `
.batch-export-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.batch-export-modal {
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  width: 500px;
  max-width: 90%;
  max-height: 80vh;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  position: relative;
  display: flex;
  flex-direction: column;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

.loading-message {
  color: #666;
  font-size: 16px;
  margin: 0;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.batch-export-success {
  margin: 20px 0;
  padding: 15px;
  background-color: #f8fff8;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
}

.batch-export-batch-ids {
  margin-top: 15px;
}

.batch-export-field {
  margin-bottom: 10px;
}

.batch-export-field label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.batch-export-textbox {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.batch-export-error {
  color: #d32f2f;
  background-color: #fde8e8;
  padding: 10px;
  border-radius: 4px;
  margin: 10px 0;
}

.batch-export-retry {
  margin-top: 8px;
  padding: 6px 12px;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.batch-export-retry:hover {
  background-color: #1565c0;
}
`;

const styleTag = document.createElement('style');
styleTag.innerHTML = styles;
document.head.appendChild(styleTag);

export default V2BatchExportModal;